import { memo } from 'react';
import styled, { css } from 'styled-components';
import type { DirectionType } from '../../../types';
import { Icon } from '../common';

interface PairSliderDividerRawProps {
  size: number;
  direction: DirectionType;
  compact: boolean;
  withArrows: boolean;
  color?: string;
}

function PairSliderDividerRaw({
  size,
  direction,
  compact,
  withArrows,
  color
}: PairSliderDividerRawProps) {
  const isDirectionHorizontal = direction === 'horizontal';

  const iconArrowType = compact ? 'arrow-solid-small' : 'arrow-solid-medium';
  const cursorType = isDirectionHorizontal ? 'ew-resize' : 'ns-resize';
  const arrowMargin = isDirectionHorizontal ? 6 : 0;

  return (
    <Container _elementDirection={direction}>
      {withArrows && (
        <Arrow _elementPaddingRight={arrowMargin} _elementCursor={cursorType}>
          <Icon
            type={iconArrowType}
            rotation={isDirectionHorizontal ? 180 : -90}
            color={color}
          />
        </Arrow>
      )}

      <Bar
        _elementDirection={direction}
        _elementSize={size}
        _elementBackgroundColor={color}
        _elementCursor={cursorType}
      />

      {withArrows && (
        <Arrow _elementPaddingLeft={arrowMargin} _elementCursor={cursorType}>
          <Icon
            type={iconArrowType}
            rotation={isDirectionHorizontal ? undefined : 90}
            color={color}
          />
        </Arrow>
      )}
    </Container>
  );
}

const Container = styled.div<{
  _elementDirection: DirectionType;
}>`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;

  ${({ _elementDirection }) =>
    _elementDirection === 'horizontal'
      ? css`
          flex-direction: row;
          height: 100%;
        `
      : css`
          flex-direction: column;
          width: 100%;
        `};
`;

const Bar = styled.div<{
  _elementDirection: DirectionType;
  _elementSize: number;
  _elementBackgroundColor?: string;
  _elementCursor?: string;
}>`
  ${({ _elementDirection, _elementSize }) =>
    _elementDirection === 'horizontal'
      ? css`
          width: ${_elementSize || 1}px;
          height: 100%;
        `
      : css`
          width: 100%;
          height: ${_elementSize || 1}px;
        `};

  ${({ _elementSize }) =>
    _elementSize > 0 &&
    css`
      box-shadow: 0 0 3px 0 rgba(0, 0, 0, 0.3);
    `};

  ${({ _elementSize, _elementBackgroundColor: backgroundColor }) =>
    _elementSize && backgroundColor && css({ backgroundColor })};

  ${({ _elementCursor: cursor }) => css({ cursor })};
`;

const Arrow = styled.div<{
  _elementPaddingRight?: number;
  _elementPaddingLeft?: number;
  _elementCursor?: string;
}>`
  ${({
    _elementPaddingRight: paddingRight,
    _elementPaddingLeft: paddingLeft,
    _elementCursor: cursor
  }) =>
    css({
      paddingRight,
      paddingLeft,
      cursor
    })};

  & svg path {
    filter: drop-shadow(0 0 3px rgba(0, 0, 0, 0.2));
  }
`;

export const PairSliderDivider = memo(PairSliderDividerRaw);
